import { Injectable, OnDestroy } from '@angular/core';
import MapView from '@arcgis/core/views/MapView';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Graphic from '@arcgis/core/Graphic';
import Sketch from '@arcgis/core/widgets/Sketch';
import Point from '@arcgis/core/geometry/Point';
import Polyline from '@arcgis/core/geometry/Polyline';
import Polygon from '@arcgis/core/geometry/Polygon';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';
import SimpleLineSymbol from '@arcgis/core/symbols/SimpleLineSymbol';
import SimpleFillSymbol from '@arcgis/core/symbols/SimpleFillSymbol';
import TextSymbol from '@arcgis/core/symbols/TextSymbol';
import Color from '@arcgis/core/Color';
import { IHandle } from '../../../../shared/types/i-handle';
import { GraphicTool, GraphicState } from './graphic-state';

export type OnGraphicAdd = (id: number, type: GraphicTool, label: string) => void;
export type OnGraphicSelect = (id: number) => void;
export type OnLiveUpdate = (label: string | null) => void;

export interface GraphicRecord {
  id: number;
  type: GraphicTool;
  geometry: Point | Polyline | Polygon;
  graphic: Graphic;
}

let nextId = 1;

const DEFAULT_SYMBOLS: GraphicState.SymbolOptions = {
  fillColor: '#2563eb',
  fillOpacity: 0.15,
  outlineColor: '#2563eb',
  outlineWidth: 2.5,
  textSize: 13,
  textBold: true,
  textItalic: false,
  textHaloColor: '#16a34a',
  textHaloSize: 3,
};

@Injectable()
export class GraphicService implements OnDestroy {
  private view: MapView | null = null;
  private graphicsLayer: GraphicsLayer | null = null;
  private sketch: Sketch | null = null;
  private createHandle: IHandle | null = null;
  private clickHandle: IHandle | null = null;
  private selectHandle: IHandle | null = null;
  private onAdd: OnGraphicAdd | null = null;
  private onSelect: OnGraphicSelect | null = null;
  private onLiveUpdate: OnLiveUpdate | null = null;
  private symbolOptions: GraphicState.SymbolOptions = { ...DEFAULT_SYMBOLS };
  private currentTool: GraphicTool | null = null;
  private textContent: string = '';

  readonly records: GraphicRecord[] = [];

  get initialized(): boolean { return !!this.sketch; }

  setSymbolOptions(opts: GraphicState.SymbolOptions): void {
    this.symbolOptions = opts;
    this.syncSketchSymbols();
  }

  initialize(view: MapView): void {
    if (this.sketch) return;
    this.view = view;

    this.graphicsLayer = new GraphicsLayer({
      title: 'graphic-layer',
      listMode: 'hide',
    });
    view.map?.add(this.graphicsLayer);

    this.sketch = new Sketch({
      view,
      layer: this.graphicsLayer,
      creationMode: 'single',
      defaultUpdateOptions: { enableRotation: false, enableScaling: false },
      visibleElements: {
        createTools: { polygon: false, polyline: false, point: false, circle: false, rectangle: false, text: false, freehandPolygon: false, freehandPolyline: false, multipoint: false } as any,
        selectionTools: { 'lasso-selection': false, 'rectangle-selection': false } as any,
        undoRedoMenu: false,
        settingsMenu: false,
      },
    });

    this.syncSketchSymbols();
    this.createHandle = this.sketch.on('create', (e) => this.onCreate(e));

    this.selectHandle = view.on('click', (event) => {
      if (this.currentTool) return;
      view.hitTest(event).then(response => {
        for (const result of response.results) {
          if ((result as any).type !== 'graphic') continue;
          const g = (result as any).graphic as Graphic;
          if ((g as any).layer === this.graphicsLayer) {
            const record = this.records.find(r => r.graphic === g);
            if (record) { this.onSelect?.(record.id); break; }
          }
        }
      }).catch(() => {});
    });
  }

  setOnAdd(cb: OnGraphicAdd): void {
    this.onAdd = cb;
  }

  setOnSelect(cb: OnGraphicSelect): void {
    this.onSelect = cb;
  }

  setOnLiveUpdate(cb: OnLiveUpdate): void {
    this.onLiveUpdate = cb;
  }

  setTextContent(text: string): void {
    this.textContent = text;
  }

  zoomTo(id: number): void {
    if (!this.view) return;
    const rec = this.records.find(r => r.id === id);
    if (!rec) return;
    this.view.goTo(rec.geometry).catch(() => {});
  }

  activateTool(tool: GraphicTool, textContent?: string): void {
    if (!this.view || !this.sketch) return;
    this.deactivate();
    this.currentTool = tool;

    if (tool === 'text') {
      this.textContent = textContent ?? 'Nota';
      this.activateText();
      return;
    }

    this.sketch.viewModel.cancel();
    this.syncSketchSymbols();
    const sketchTool = tool === 'polyline' ? 'polyline'
      : tool === 'polygon' ? 'polygon'
      : tool === 'rectangle' ? 'rectangle'
      : tool === 'circle' ? 'circle'
      : tool === 'point' ? 'point'
      : 'polyline';
    this.sketch.viewModel.create(sketchTool as any).catch(() => {});
  }

  deactivate(): void {
    this.sketch?.viewModel?.cancel();
    this.clearTransientState();
    this.currentTool = null;
    this.onLiveUpdate?.(null);
  }

  removeItem(id: number): void {
    const idx = this.records.findIndex(r => r.id === id);
    if (idx === -1) return;
    const rec = this.records[idx];
    this.graphicsLayer?.remove(rec.graphic);
    this.records.splice(idx, 1);
  }

  clearAll(): void {
    for (const rec of this.records) {
      this.graphicsLayer?.remove(rec.graphic);
    }
    this.records.length = 0;
  }

  private activateText(): void {
    if (!this.view || !this.sketch) return;
    this.sketch.viewModel.cancel();
    const container = this.view.container as HTMLElement | null;
    if (container) container.style.cursor = 'crosshair';
    this.onLiveUpdate?.('Haz clic en el mapa para colocar el texto.');
    this.clickHandle = this.view.on('click', (e) => {
      const pt = this.view!.toMap({ x: e.x, y: e.y }) as Point;
      if (!pt) return;
      const id = nextId++;
      const opts = this.symbolOptions;
      const g = new Graphic({
        geometry: pt,
        symbol: new TextSymbol({
          text: this.textContent,
          color: new Color('#ffffff'),
          haloColor: new Color(opts.textHaloColor),
          haloSize: opts.textHaloSize,
          font: {
            size: opts.textSize,
            weight: opts.textBold ? 'bold' : 'normal',
            style: opts.textItalic ? 'italic' : 'normal',
          },
          xoffset: 0,
          yoffset: -(opts.textSize + 4),
        }),
      });
      this.graphicsLayer?.add(g);
      this.records.push({ id, type: 'text', geometry: pt, graphic: g });
      this.onLiveUpdate?.(null);
      this.onAdd?.(id, 'text', this.textContent);
    });
  }

  private onCreate(event: any): void {
    if (event.state === 'start') {
      this.onLiveUpdate?.(null);
      return;
    }
    if (event.state === 'active') {
      const geo = event.graphic?.geometry;
      if (!geo) { this.onLiveUpdate?.(null); return; }
      const ext = geo.extent;
      let label = '';
      if (this.currentTool === 'rectangle' && ext) {
        const w = (ext.xmax - ext.xmin).toFixed(1);
        const h = (ext.ymax - ext.ymin).toFixed(1);
        label = `${w} × ${h} m`;
      } else if (this.currentTool === 'circle' && ext) {
        const r = ((ext.xmax - ext.xmin) / 2).toFixed(1);
        label = `r = ${r} m`;
      } else if (geo.type === 'polyline') {
        const paths = (geo as any).paths ?? [];
        label = `${paths[0]?.length ?? 0} vértices`;
      } else if (geo.type === 'polygon') {
        const rings = (geo as any).rings ?? [];
        label = `${rings[0]?.length ?? 0} vértices`;
      }
      this.onLiveUpdate?.(label || null);
      return;
    }
    if (event.state !== 'complete') return;
    this.onLiveUpdate?.(null);
    const graphic = event.graphic as Graphic;
    const geo = graphic?.geometry;
    if (!geo) return;

    this.applyUserSymbol(graphic);

    const tool = this.mapType(geo.type);
    const id = nextId++;
    const label = this.labelFor(tool);
    this.records.push({ id, type: tool, geometry: geo as any, graphic });
    this.onAdd?.(id, tool, label);
  }

  private applyUserSymbol(graphic: Graphic): void {
    const opts = this.symbolOptions;
    const t = graphic.geometry?.type;

    if (t === 'polygon' || t === 'extent') {
      graphic.symbol = new SimpleFillSymbol({
        color: hexa(opts.fillColor, opts.fillOpacity),
        outline: new SimpleLineSymbol({
          color: opts.outlineColor,
          width: opts.outlineWidth,
          cap: 'round' as any,
          join: 'round' as any,
        }),
      });
    } else if (t === 'polyline') {
      graphic.symbol = new SimpleLineSymbol({
        color: opts.outlineColor,
        width: opts.outlineWidth,
        cap: 'round' as any,
        join: 'round' as any,
      });
    } else if (t === 'point' || t === 'multipoint') {
      graphic.symbol = new SimpleMarkerSymbol({
        color: hexa(opts.fillColor, 1),
        outline: new SimpleLineSymbol({
          color: opts.outlineColor,
          width: Math.min(opts.outlineWidth, 3),
        }),
        size: 14 + opts.outlineWidth * 1.5,
        style: 'circle',
      });
    }
  }

  private syncSketchSymbols(): void {
    if (!this.sketch) return;
    const opts = this.symbolOptions;
    const lineSym = new SimpleLineSymbol({
      color: opts.outlineColor,
      width: opts.outlineWidth,
      cap: 'round' as any,
      join: 'round' as any,
    });
    this.sketch.polylineSymbol = lineSym;
    this.sketch.polygonSymbol = new SimpleFillSymbol({
      color: hexa(opts.fillColor, opts.fillOpacity),
      outline: lineSym,
    });
    this.sketch.pointSymbol = new SimpleMarkerSymbol({
      color: hexa(opts.fillColor, 1),
      outline: new SimpleLineSymbol({ color: opts.outlineColor, width: Math.min(opts.outlineWidth, 3) }),
      size: 14 + opts.outlineWidth * 1.5,
      style: 'circle',
    });
  }

  private mapType(geomType: string): GraphicTool {
    switch (geomType) {
      case 'polygon': return 'polygon';
      case 'polyline': return 'polyline';
      case 'extent': return 'rectangle';
      case 'point': case 'multipoint': return 'point';
      default: return 'polygon';
    }
  }

  private labelFor(tool: GraphicTool): string {
    switch (tool) {
      case 'polygon': return 'Polígono';
      case 'polyline': return 'Línea';
      case 'circle': return 'Círculo';
      case 'rectangle': return 'Rectángulo';
      case 'point': return 'Punto';
      case 'text': return 'Nota';
    }
  }

  private clearTransientState(): void {
    this.clickHandle?.remove();
    const container = this.view?.container as HTMLElement | null;
    if (container) container.style.cursor = 'default';
  }

  ngOnDestroy(): void {
    this.createHandle?.remove();
    this.selectHandle?.remove();
    this.deactivate();
    this.clearAll();
    if (this.graphicsLayer) {
      this.view?.map?.remove(this.graphicsLayer);
    }
  }
}

function hexa(hex: string, alpha: number): string {
  const a = Math.round(Math.max(0, Math.min(1, alpha)) * 255).toString(16).padStart(2, '0');
  return `${hex}${a}`;
}
